import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import { AuthContext } from '@dashboard/context/AuthContext';

import Button from '@dashboard/components/Button';
import Spinner from '@dashboard/components/Spinner';

import { fetchPaymentMethod } from './helpers';
import { Alert, AlertDescription } from '@/dashboard/components/ui/alert';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import FormSubmit from '@dashboard/components/Form/components/FormSubmit';
import { FormElement } from '@dashboard/components/Form';

const PaymentMethod = ({ setEditing, hasBillingAddress = true }) => {
  const { seller, sellerId, token } = useContext(AuthContext);

  const planHandle = seller && seller.plan && seller.plan.handle;

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (paymentMethod) return;

    async function getPaymentMethod() {
      try {
        const { data, error: err } = await fetchPaymentMethod(sellerId, token);

        if (err) throw new Error(err);

        setPaymentMethod(data);
        setLoading(false);
        setError('');
      } catch (err) {
        setError(err.message || 'Unable to fetch payment method');
        setLoading(false);
      }
    }

    getPaymentMethod();
  }, [paymentMethod, planHandle]);

  if (loading) {
    return <Spinner />;
  }

  if (paymentMethod) {
    return (
      <>
        {error && (
          <FormElement>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </FormElement>
        )}

        <div className="flex flex-row mb-2">
          <div className="font-semibold w-64">Card number:</div>
          <div>{`${paymentMethod.card.brand} (**** **** **** ${paymentMethod.card.last4})`}</div>
        </div>

        <div className="flex flex-row mb-2">
          <div className="font-semibold w-64">Expires:</div>
          <div>{`${paymentMethod.card.exp_month} / ${paymentMethod.card.exp_year}`}</div>
        </div>

        <FormSubmit>
          <Button appearance="secondary" handleClick={() => setEditing(true)}>
            Update card
          </Button>
        </FormSubmit>
      </>
    );
  }

  if (!paymentMethod) {
    return (
      <>
        <FormElement>
          <div className="text-sm">{`You haven't added a payment method yet`}</div>
        </FormElement>

        {!hasBillingAddress && (
          <Alert className="mb-4">
            <InformationCircleIcon />
            <AlertDescription>Add your business address before adding a card.</AlertDescription>
          </Alert>
        )}

        {error && (
          <FormElement>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </FormElement>
        )}

        <FormSubmit>
          <Button
            appearance="secondary"
            handleClick={() => setEditing(true)}
            disabled={!hasBillingAddress}
          >
            Add card
          </Button>
        </FormSubmit>
      </>
    );
  }
};

PaymentMethod.propTypes = {
  setEditing: PropTypes.func.isRequired,
  hasBillingAddress: PropTypes.bool,
};

export default PaymentMethod;
